<template>
  <div class="assignment-data-linker">
    <!-- Step 1: Notice -->
    <div v-if="step === 1" class="step step-notice">
      <div class="notice-icon">⚠️</div>
      <h3>Tehtävätiedot puuttuvat</h3>
      <p>
        Tälle tehtävälle ei ole vielä linkitetty tietoja. Hae tehtävän tiedot
        koulun intranetistä ja syötä ne lomakkeeseen.
      </p>
      <a
        href="https://sites.google.com/teho-opisto.fi/intranet/opetusmateriaali/kotitehtävät"
        target="_blank"
        rel="noopener noreferrer"
        class="intranet-link"
      >
        Avaa intranet →
      </a>
      <button class="btn-primary" @click="step = 2">Syötä tiedot</button>
    </div>

    <!-- Step 2: Data entry form -->
    <div v-else-if="step === 2" class="step step-form">
      <h3>Tehtävän tiedot</h3>

      <div class="field">
        <label for="otsikko">Otsikko <span class="required">*</span></label>
        <input
          id="otsikko"
          v-model="form.otsikko"
          type="text"
          placeholder="esim. Tehtävä 3 — Tarinakirjoitus"
          class="text-input"
        />
      </div>

      <div class="field">
        <label for="alaotsikko">Alaotsikko</label>
        <input
          id="alaotsikko"
          v-model="form.alaotsikko"
          type="text"
          placeholder="Valinnainen tarkentava otsikko"
          class="text-input"
        />
      </div>

      <div class="field">
        <label for="lukukausi">Lukukausi <span class="required">*</span></label>
        <input
          id="lukukausi"
          v-model="form.lukukausi"
          type="text"
          placeholder="esim. kevät2026"
          class="text-input"
        />
      </div>

      <div class="field">
        <label for="tehtavananto"
          >Tehtävänanto <span class="required">*</span></label
        >
        <textarea
          id="tehtavananto"
          v-model="form.tehtavananto"
          placeholder="Kirjoita tehtävänanto tähän..."
          class="textarea-input"
          rows="6"
        ></textarea>
      </div>

      <div class="field">
        <label>Arviointiperusteet <span class="required">*</span></label>
        <div
          v-for="(criterion, index) in form.arviointiperusteet"
          :key="index"
          class="criterion-row"
        >
          <input
            v-model="form.arviointiperusteet[index]"
            type="text"
            :placeholder="`Peruste ${index + 1}`"
            class="text-input criterion-input"
            @keydown.enter.prevent="addCriterion"
          />
          <button
            class="btn-icon btn-remove"
            @click="removeCriterion(index)"
            :disabled="form.arviointiperusteet.length <= 1"
            title="Poista peruste"
          >
            ×
          </button>
        </div>
        <button class="btn-secondary btn-add-criterion" @click="addCriterion">
          + Lisää arviointiperuste
        </button>
      </div>

      <div class="field">
        <label>Kuvat (valinnainen)</label>
        <input
          ref="fileInput"
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp"
          class="file-input-hidden"
          @change="onFilesSelected"
        />
        <button type="button" class="btn-secondary" @click="fileInput?.click()">
          Valitse kuvat...
        </button>
        <div v-if="selectedFiles.length > 0" class="selected-files">
          <span
            v-for="file in selectedFiles"
            :key="file.name"
            class="file-chip"
          >
            {{ file.name }}
          </span>
        </div>
      </div>

      <p v-if="formError" class="error-message">{{ formError }}</p>

      <div class="step-actions">
        <button class="btn-ghost" @click="step = 1">← Takaisin</button>
        <button
          class="btn-primary"
          @click="saveDraftAndAdvance"
          :disabled="isSaving"
        >
          {{ isSaving ? "Tallennetaan..." : "Seuraava →" }}
        </button>
      </div>
    </div>

    <!-- Step 3: Review & confirm -->
    <div v-else-if="step === 3" class="step step-review">
      <h3>Tarkista tiedot ennen tallentamista</h3>

      <div class="review-field">
        <span class="review-label">Otsikko</span>
        <span class="review-value">{{ form.otsikko }}</span>
      </div>
      <div v-if="form.alaotsikko" class="review-field">
        <span class="review-label">Alaotsikko</span>
        <span class="review-value">{{ form.alaotsikko }}</span>
      </div>
      <div class="review-field">
        <span class="review-label">Lukukausi</span>
        <span class="review-value">{{ form.lukukausi }}</span>
      </div>

      <div class="review-field review-field--block">
        <span class="review-label">Tehtävänanto</span>
        <pre class="review-text">{{ form.tehtavananto }}</pre>
      </div>

      <div class="review-field review-field--block">
        <span class="review-label">Arviointiperusteet</span>
        <ol class="review-list">
          <li
            v-for="(criterion, index) in form.arviointiperusteet"
            :key="index"
          >
            {{ criterion }}
          </li>
        </ol>
      </div>

      <div v-if="uploadedImageUrls.length > 0" class="review-field">
        <span class="review-label">Kuvat</span>
        <span class="review-value"
          >{{ uploadedImageUrls.length }} kuva{{
            uploadedImageUrls.length !== 1 ? "a" : ""
          }}
          ladattu</span
        >
      </div>

      <div class="immutability-warning">
        ⚠️ Tallentamisen jälkeen tietoja ei voi muokata.
      </div>

      <p v-if="confirmError" class="error-message">{{ confirmError }}</p>

      <div class="step-actions">
        <button class="btn-ghost" @click="step = 2" :disabled="isConfirming">
          ← Takaisin
        </button>
        <button
          class="btn-confirm"
          @click="confirmAssignment"
          :disabled="isConfirming"
        >
          {{ isConfirming ? "Tallennetaan..." : "Tallenna pysyvästi" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { apiUrl } from "@/config/api";

const props = defineProps<{
  learningUnitId: string;
  existingAssignmentId?: number;
  existingData?: {
    otsikko: string;
    alaotsikko: string | null;
    lukukausi: string;
    tehtävänanto: string;
    arviointiperusteet: string[];
    kuvat: string[];
  };
}>();

const emit = defineEmits<{
  confirmed: [
    assignmentDetails: {
      id: number;
      isConfirmed: boolean;
      tehtävänanto: string;
      arviointiperusteet: string[];
      kuvat: string[];
      peerGalleryDriveLink: null;
    },
  ];
}>();

const step = ref<1 | 2 | 3>(props.existingData ? 3 : 1);
const draftAssignmentId = ref<number | null>(
  props.existingAssignmentId ?? null,
);
const uploadedImageUrls = ref<string[]>(props.existingData?.kuvat ?? []);

const form = ref({
  otsikko: props.existingData?.otsikko ?? "",
  alaotsikko: props.existingData?.alaotsikko ?? "",
  lukukausi: props.existingData?.lukukausi ?? "kevät2026",
  tehtavananto: props.existingData?.tehtävänanto ?? "",
  arviointiperusteet: props.existingData?.arviointiperusteet.length
    ? [...props.existingData.arviointiperusteet]
    : [""],
});

const selectedFiles = ref<File[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);

const isSaving = ref(false);
const isConfirming = ref(false);
const formError = ref("");
const confirmError = ref("");

const addCriterion = () => {
  form.value.arviointiperusteet.push("");
};

const removeCriterion = (index: number) => {
  form.value.arviointiperusteet.splice(index, 1);
};

const onFilesSelected = (event: Event) => {
  const input = event.target as HTMLInputElement;
  selectedFiles.value = input.files ? Array.from(input.files) : [];
};

const validateForm = (): boolean => {
  if (!form.value.otsikko.trim()) {
    formError.value = "Otsikko on pakollinen.";
    return false;
  }
  if (!form.value.lukukausi.trim()) {
    formError.value = "Lukukausi on pakollinen.";
    return false;
  }
  if (!form.value.tehtavananto.trim()) {
    formError.value = "Tehtävänanto on pakollinen.";
    return false;
  }
  const filled = form.value.arviointiperusteet.filter((c) => c.trim());
  if (filled.length === 0) {
    formError.value = "Vähintään yksi arviointiperuste on pakollinen.";
    return false;
  }
  formError.value = "";
  return true;
};

const saveDraftAndAdvance = async () => {
  if (!validateForm()) return;

  isSaving.value = true;
  try {
    const criteria = form.value.arviointiperusteet.filter((c) => c.trim());

    if (draftAssignmentId.value === null) {
      // Create new assignment and link to learning unit
      const res = await fetch(apiUrl("/assignments"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          learningUnitId: props.learningUnitId,
          otsikko: form.value.otsikko.trim(),
          alaotsikko: form.value.alaotsikko.trim() || undefined,
          tehtavananto: form.value.tehtavananto.trim(),
          arviointiperusteet: criteria,
          lukukausi: form.value.lukukausi.trim(),
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error || "Virhe luotaessa tehtävää");
      }
      const data = (await res.json()) as { assignment: { id: number } };
      draftAssignmentId.value = data.assignment.id;
    } else {
      // Update existing draft
      const res = await fetch(
        apiUrl(`/assignments/${draftAssignmentId.value}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            otsikko: form.value.otsikko.trim(),
            alaotsikko: form.value.alaotsikko.trim() || null,
            tehtavananto: form.value.tehtavananto.trim(),
            arviointiperusteet: criteria,
            lukukausi: form.value.lukukausi.trim(),
          }),
        },
      );
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error || "Virhe tallennettaessa tehtävää");
      }
    }

    // Upload images if any were selected
    if (selectedFiles.value.length > 0) {
      const formData = new FormData();
      selectedFiles.value.forEach((file) => formData.append("images", file));

      const imgRes = await fetch(
        apiUrl(`/assignments/${draftAssignmentId.value}/images`),
        {
          method: "POST",
          credentials: "include",
          body: formData,
        },
      );
      if (!imgRes.ok) {
        let errMsg: string | undefined;
        const contentType = imgRes.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const err = (await imgRes.json()) as { error?: string };
          errMsg = err.error;
        }
        if (!errMsg) {
          errMsg =
            imgRes.status === 413
              ? "Kuvat ovat liian suuria. Pienennä kuvia tai lataa vähemmän kerralla."
              : "Virhe kuvien lataamisessa";
        }
        throw new Error(errMsg);
      }
      const imgData = (await imgRes.json()) as { urls: string[] };
      uploadedImageUrls.value = [...uploadedImageUrls.value, ...imgData.urls];
      selectedFiles.value = [];
      if (fileInput.value) fileInput.value.value = "";
    }

    step.value = 3;
  } catch (error) {
    formError.value =
      error instanceof Error ? error.message : "Tuntematon virhe";
  } finally {
    isSaving.value = false;
  }
};

const confirmAssignment = async () => {
  if (draftAssignmentId.value === null) return;

  isConfirming.value = true;
  confirmError.value = "";

  try {
    const res = await fetch(
      apiUrl(`/assignments/${draftAssignmentId.value}/confirm`),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      },
    );
    if (!res.ok) {
      const err = (await res.json()) as { error?: string };
      throw new Error(err.error || "Virhe vahvistettaessa tehtävää");
    }

    const criteria = form.value.arviointiperusteet.filter((c) => c.trim());
    emit("confirmed", {
      id: draftAssignmentId.value,
      isConfirmed: true,
      tehtävänanto: form.value.tehtavananto.trim(),
      arviointiperusteet: criteria,
      kuvat: uploadedImageUrls.value,
      peerGalleryDriveLink: null,
    });
  } catch (error) {
    confirmError.value =
      error instanceof Error ? error.message : "Tuntematon virhe";
  } finally {
    isConfirming.value = false;
  }
};
</script>

<style scoped>
.assignment-data-linker {
  background-color: #fff8e1;
  border: 1px solid #f9a825;
  border-radius: 8px;
  padding: 1.5rem;
}

.step h3 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
}

/* Step 1 — notice */
.step-notice {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.notice-icon {
  font-size: 2rem;
  line-height: 1;
}

.intranet-link {
  display: inline-block;
  color: var(--color-primary, #1976d2);
  font-weight: 600;
  text-decoration: none;
}

.intranet-link:hover {
  text-decoration: underline;
}

/* Step 2 — form */
.field {
  margin-bottom: 1.25rem;
}

.field label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
}

.required {
  color: #c62828;
}

.text-input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: var(--color-primary, #1976d2);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.textarea-input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.textarea-input:focus {
  outline: none;
  border-color: var(--color-primary, #1976d2);
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.criterion-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.criterion-input {
  flex: 1;
}

.btn-icon {
  padding: 0 0.6rem;
  height: 36px;
  border-radius: 4px;
  border: 1px solid #ced4da;
  background: white;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  color: #6c757d;
  flex-shrink: 0;
}

.btn-remove:hover:not(:disabled) {
  background: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.btn-remove:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-add-criterion {
  margin-top: 0.25rem;
  font-size: 0.85rem;
}

.file-input-hidden {
  display: none;
}

.selected-files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.file-chip {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 12px;
  padding: 0.2rem 0.6rem;
  font-size: 0.8rem;
  color: #1565c0;
}

/* Step 3 — review */
.review-field {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
  align-items: baseline;
}

.review-field--block {
  flex-direction: column;
  gap: 0.25rem;
}

.review-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #6c757d;
  flex-shrink: 0;
  min-width: 7rem;
}

.review-value {
  font-size: 0.95rem;
}

.review-text {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.75rem;
  font-family: inherit;
  font-size: 0.9rem;
  white-space: pre-wrap;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.review-list {
  margin: 0;
  padding-left: 1.5rem;
}

.review-list li {
  margin-bottom: 0.25rem;
}

.immutability-warning {
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  margin: 1rem 0;
}

/* Shared action buttons */
.step-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 1.25rem;
}

.btn-primary {
  padding: 0.6rem 1.25rem;
  background: var(--color-primary, #1976d2);
  color: white;
  border: 1px solid var(--color-primary, #1976d2);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-dark, #1565c0);
  border-color: var(--color-primary-dark, #1565c0);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  background: white;
  color: var(--color-primary, #1976d2);
  border: 1px solid var(--color-primary, #1976d2);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #e3f2fd;
}

.btn-ghost {
  padding: 0.6rem 1rem;
  background: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.btn-ghost:hover:not(:disabled) {
  background: #f8f9fa;
}

.btn-ghost:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-confirm {
  padding: 0.6rem 1.25rem;
  background: #2e7d32;
  color: white;
  border: 1px solid #2e7d32;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  transition: all 0.2s ease;
}

.btn-confirm:hover:not(:disabled) {
  background: #1b5e20;
  border-color: #1b5e20;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: #c62828;
  font-size: 0.9rem;
  margin: 0.5rem 0 0;
}
</style>
